/**
 * Public page shell for the customer-facing legal documents (/terms,
 * /privacy, /refund-policy, /delete-account). The copy itself lives in @finmin/ui so the
 * provider app renders the same Privacy Policy.
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { BrandLogo, LEGAL_COMPANY, LegalDocument, type LegalDoc } from '@finmin/ui';

const POLICY_LINKS = [
  { to: '/terms', label: 'Terms of Use' },
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/refund-policy', label: 'Refund & Cancellation Policy' },
  { to: '/delete-account', label: 'Delete Account' },
] as const;

export function LegalPage({ doc }: { doc: LegalDoc }) {
  // Links between policies keep the previous scroll offset otherwise. The
  // title is restored on leave because GA page views read document.title.
  React.useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = `${doc.title} | FinMin`;
    return () => {
      document.title = previousTitle;
    };
  }, [doc]);

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border bg-background/95 sticky top-0 z-10 border-b backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" aria-label="FinMin home">
            <BrandLogo height={26} />
          </Link>
          <Link
            to="/"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        <LegalDocument doc={doc} />
      </main>

      <footer className="border-border border-t">
        <div className="text-muted-foreground mx-auto flex max-w-6xl flex-col gap-3 px-4 py-6 text-xs sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-2">
            {POLICY_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="hover:text-foreground transition-colors"
                activeProps={{ className: 'font-semibold text-foreground' }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <p>
            © {new Date().getFullYear()} {LEGAL_COMPANY.entity}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
